/**
 * Mothers waiting on a doula, and what became of every ask.
 *
 * WHY THIS EXISTS
 * The one real match request in production sat in 'selected' for three weeks
 * with nobody able to answer it, and no screen in the platform listed it. The
 * fan-out (TRUS-679) makes an ask reachable; this makes a STALLED ask visible,
 * which is the half a person has to act on.
 *
 * THE COLUMN THAT MATTERS IS "REACHABLE"
 * 24 of 25 contracted doulas have no account and the panel had no phone or
 * email until this release. So the common failure is not "she has not replied"
 * but "she was never actually sent anything" — and on a screen that shows only
 * a response status those two look identical. Each row says which channel could
 * reach her, or says plainly that none can.
 *
 * RESEND CHASES ONLY THE UNANSWERED. A doula who declined is not pestered and
 * one who accepted is not asked twice; the server enforces that, and the button
 * says so rather than implying it sends to everyone.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { PageState } from '@/components/weavein';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, Clock, Loader2, Send, UserCheck } from 'lucide-react';

interface MatchOffer {
  panel_id: number;
  display_name: string;
  rank: number;
  match_score: number | null;
  response_status: string;
  responded_at: string | null;
  notified_at: string | null;
  notification_method: string | null;
  notification_count: number;
  decline_reason: string | null;
  reachable_by: string | null;
}

interface MatchRequest {
  request_id: number;
  member_id: number;
  member_first_name: string | null;
  status: string;
  created_at: string | null;
  updated_at: string | null;
  due_date: string | null;
  asked: number;
  answered: number;
  notified: number;
  waiting_days: number | null;
  offers: MatchOffer[];
}

function answerTone(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'accepted') return 'default';
  if (status === 'declined') return 'destructive';
  // 'closed' is NOT a refusal — she was set aside when someone else accepted
  // first. Showing it in the same colour as a decline would misreport her.
  return 'outline';
}

export default function MatchConsole() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<MatchRequest[]>([]);
  const [open, setOpen] = useState<MatchRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendNote, setResendNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await authFetchJson<MatchRequest[]>('/api/doulas/match-requests?waiting_only=true'));
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t('doula.matchQueueFailed', 'We could not load the match queue.'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const openRequest = async (requestId: number) => {
    setResendNote(null);
    try {
      setOpen(await authFetchJson<MatchRequest>(`/api/doulas/match-requests/${requestId}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doula.matchOpenFailed', 'We could not open that request.'));
    }
  };

  const resend = async () => {
    if (!open) return;
    setResending(true);
    setResendNote(null);
    try {
      const out = await authFetchJson<{ asked: number; delivered: number; unreachable: number[] }>(
        `/api/doulas/match-requests/${open.request_id}/resend`,
        { method: 'POST' },
      );
      // Report the unreachable count rather than a bare success. "Sent" when
      // nothing left the building is the failure this whole screen exists to
      // stop being invisible.
      setResendNote(
        out.delivered > 0
          ? t('doula.resendSome', '{{delivered}} of {{asked}} reached.', {
              delivered: out.delivered,
              asked: out.asked,
            })
          : t('doula.resendNone', 'Nobody could be reached. Add a phone number or email to their panel record.'),
      );
      await openRequest(open.request_id);
      await load();
    } catch (e) {
      setResendNote(e instanceof Error ? e.message : t('doula.resendFailed', 'The resend did not go through.'));
    } finally {
      setResending(false);
    }
  };

  if (loading) return <PageState variant="loading" />;
  if (error) return <PageState variant="error" title={error} />;

  if (!rows.length) {
    return (
      <PageState
        variant="empty"
        icon={<UserCheck className="h-6 w-6 text-ink-300" />}
        title={t('doula.noWaiting', 'No mother is waiting on a doula')}
        message={t('doula.noWaitingHelp', 'Requests appear here while they are out for an answer.')}
      />
    );
  }

  return (
    <div className="space-y-4">
      {rows.map((r) => {
        const unanswered = r.asked - r.answered;
        const neverSent = r.notified === 0 && r.asked > 0;
        return (
          <div key={r.request_id} className="rounded-lg border p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">
                {r.member_first_name || t('doula.aMother', 'A mother')}
              </span>
              <Badge variant="outline">{r.status}</Badge>
              {r.waiting_days !== null && r.waiting_days >= 2 && (
                <Badge variant="destructive" className="gap-1">
                  <Clock className="h-3 w-3" />
                  {t('doula.waitingDays', 'waiting {{count}} days', { count: r.waiting_days })}
                </Badge>
              )}
              {neverSent && (
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {t('doula.neverSent', 'never sent')}
                </Badge>
              )}
            </div>

            <p className="text-sm text-muted-foreground">
              {t('doula.askSummary', '{{asked}} asked · {{notified}} reached · {{unanswered}} still to answer', {
                asked: r.asked,
                notified: r.notified,
                unanswered,
              })}
              {r.due_date ? ` · ${t('doula.due', 'due')} ${r.due_date}` : ''}
            </p>

            <Button variant="outline" size="sm" onClick={() => void openRequest(r.request_id)}>
              {t('doula.seeAsks', 'See the asks')}
            </Button>

            {open?.request_id === r.request_id && (
              <div className="space-y-2 border-t pt-3">
                {open.offers.map((o) => (
                  <div key={o.panel_id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">#{o.rank}</span>
                    <span>{o.display_name}</span>
                    <Badge variant={answerTone(o.response_status)}>{o.response_status}</Badge>

                    {o.notified_at ? (
                      <span className="text-muted-foreground">
                        {t('doula.sentVia', 'sent by {{method}}', { method: o.notification_method || '—' })}
                        {o.notification_count > 1
                          ? ` ×${o.notification_count}`
                          : ''}
                      </span>
                    ) : o.reachable_by ? (
                      <span className="text-muted-foreground">
                        {t('doula.notSentYet', 'not sent yet · reachable by {{channel}}', {
                          channel: o.reachable_by,
                        })}
                      </span>
                    ) : (
                      /* The actionable one. She needs a contact detail adding,
                         not another send. */
                      <Badge variant="destructive" className="gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {t('doula.noContact', 'no phone or email on file')}
                      </Badge>
                    )}

                    {o.decline_reason && (
                      <span className="text-muted-foreground">“{o.decline_reason}”</span>
                    )}
                  </div>
                ))}

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Button size="sm" onClick={() => void resend()} disabled={resending}>
                    {resending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    {t('doula.resendUnanswered', 'Ask the unanswered again')}
                  </Button>
                  {resendNote && <span className="text-sm text-muted-foreground">{resendNote}</span>}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
